// VOY-461 — CompoundResultsView (Surface 3). Renders the assembled,
// personalized trip on a vertical timeline, with the refinement system live.
// States from the design:
//   * all-components  — flight outbound / hotel / itinerary / flight return
//   * single-component — only the in-scope components, framing adapts, no empty slots
//   * mid-refinement  — per-component reload / HELD / unchanged, no full-page flash
//
// Renders purely from a VoyMyTripSession (the SSE stream fills it). Component
// cards independently reflect their `state`, so a refine reloads only the
// affected stops while others hold.

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { MatchScorePill } from './MatchScorePill'
import { RefinementBar } from './RefinementBar'
import type {
  VoyMyTripSession,
  ComponentResult,
  ItineraryDraft,
} from '@/types/voy-my-trip'
import type { ConstraintType, RefinementConstraintSet } from '@/lib/search/refinement-constraints'
import type { FlightResult, Hotel } from '@/types/travel'
import '@/styles/voy-my-trip.css'

// ---- icons (match the design's stroke set) -------------------------------
const PlaneIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" /></svg>)
const BedIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 9v12h18V9M3 9l9-7 9 7M9 21V12h6v9" /></svg>)
const PinIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>)
const CheckIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>)
const SwapIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M7 10l-4 4 4 4M3 14h14M17 14l4-4-4-4M21 10H7" /></svg>)
const WhyIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01" /></svg>)
const CartIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6" /></svg>)

// ---- small helpers -------------------------------------------------------
function money(n: number | null | undefined): string | null {
  if (typeof n !== 'number' || !Number.isFinite(n)) return null
  return `$${Math.round(n).toLocaleString()}`
}
function nightsBetween(a?: string | null, b?: string | null): number | null {
  if (!a || !b) return null
  const ms = Date.parse(b) - Date.parse(a)
  return ms > 0 ? Math.round(ms / 86400000) : null
}
function fmtDate(iso?: string | null): string {
  if (!iso) return '—'
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return iso
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1]
  return `${mon} ${Number(m[3])}`
}

/** `feature` is the prominent (gold) price; `struck` + `save` only when the
 *  member (special) price is a genuine discount under standard. */
interface PriceParts { feature: number | null; struck: number | null; save: number | null; unit: string }
/**
 * Voy's dual pricing: `standardPrice` is the non-member rate, `specialPrice`
 * the member rate. When member < standard we show the struck standard, the
 * member price (gold) and the savings (green) — the design's dual block. When
 * an offer carries no real member discount (e.g. zero-markup flights where the
 * two are equal) we show the single price in the same prominent position.
 */
function dualParts(standard: number | null, special: number | null, unit: string): PriceParts {
  const hasDiscount = standard != null && special != null && special < standard
  return {
    feature: hasDiscount ? special : (standard ?? special),
    struck: hasDiscount ? standard : null,
    save: hasDiscount ? (standard as number) - (special as number) : null,
    unit,
  }
}
function flightPriceParts(f: FlightResult): PriceParts {
  const o = f as { standardPrice?: number; totalPrice?: number; price?: number; specialPrice?: number; memberPrice?: number }
  const standard = o.standardPrice ?? o.totalPrice ?? o.price ?? null
  const special = o.specialPrice ?? o.memberPrice ?? null
  return dualParts(standard, special, 'round / pax')
}
function hotelPriceParts(h: Hotel): PriceParts {
  const o = h as { standardPrice?: number; pricePerNight?: number; price?: { amount?: number }; specialPrice?: number; memberPrice?: number }
  const standard = o.standardPrice ?? o.pricePerNight ?? o.price?.amount ?? null
  const special = o.specialPrice ?? o.memberPrice ?? null
  return dualParts(standard, special, '/ night')
}

// ---- card primitives -----------------------------------------------------
function Price({ p }: { p: PriceParts }) {
  if (p.feature == null) return null
  return (
    <div className="vmt-price">
      {p.struck != null && <div className="vmt-price__std">{money(p.struck)}</div>}
      <div className="vmt-price__mem">{money(p.feature)}</div>
      {p.save != null && p.save > 0 && <div className="vmt-price__save">− {money(p.save)} member</div>}
      <div className="vmt-price__unit">{p.unit}</div>
    </div>
  )
}

function Reasons({ reasons }: { reasons?: string[] }) {
  if (!reasons?.length) return null
  return (
    <div className="vmt-reasons">
      {reasons.slice(0, 3).map((r, i) => (
        <div key={`${i}:${r}`} className="vmt-reason"><CheckIcon /><span>{r}</span></div>
      ))}
    </div>
  )
}

interface SwapOption { key: string; title: string; sub: string; price: string | null }

/**
 * Working alternatives swap. Click to expand the list of other options; click
 * an option to swap it into the primary slot (handled by the parent's
 * onSwapAlternative — pure client-side reorder, no re-search).
 */
function AlternativesSwap({ options, onPick }: { options: SwapOption[]; onPick: (idx: number) => void }) {
  const [open, setOpen] = useState(false)
  if (!options.length) return null
  return (
    <div className="vmt-hyb-swap">
      <button type="button" className="vmt-swap" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <SwapIcon />{options.length} alternative{options.length === 1 ? '' : 's'}
      </button>
      {open && (
        <div className="vmt-swap-list" role="menu" aria-label="Alternatives">
          {options.map((o, i) => (
            <button
              key={o.key}
              type="button"
              role="menuitem"
              className="vmt-swap-opt"
              onClick={() => { onPick(i); setOpen(false) }}
            >
              <span className="vmt-swap-opt__main">
                <span className="vmt-swap-opt__nm">{o.title}</span>
                <span className="vmt-swap-opt__sub">{o.sub}</span>
              </span>
              {o.price && <span className="vmt-swap-opt__price">{o.price}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** Itinerary "Re-draft" is intentionally inert: real activity sourcing is a
 * separate piece of work, so we mark it disabled rather than ship a dead
 * button that looks live. */
function RedraftPlaceholder() {
  return (
    <div className="vmt-hyb-swap">
      <button type="button" className="vmt-swap" disabled title="Activity sourcing coming soon — this plan is a draft" style={{ opacity: 0.55, cursor: 'default' }}>
        <SwapIcon />Re-draft · coming soon
      </button>
    </div>
  )
}

function flightSwapOptions(alts: FlightResult[]): SwapOption[] {
  return (alts ?? []).map((f, i) => {
    const s = (f as { slices?: Array<{ origin?: string; destination?: string; airline?: string; stops?: number }> })?.slices?.[0]
    const airline = s?.airline ?? (f as { airline?: string })?.airline ?? 'Flight'
    const route = s ? `${s.origin ?? ''} → ${s.destination ?? ''}` : ''
    const stops = s?.stops ?? 0
    const o = f as { specialPrice?: number; standardPrice?: number; totalPrice?: number; price?: number }
    const price = o.specialPrice ?? o.standardPrice ?? o.totalPrice ?? o.price ?? null
    return { key: `f-${i}`, title: route || airline, sub: `${airline} · ${stops === 0 ? 'nonstop' : `${stops} stop`}`, price: money(price) }
  })
}
function hotelSwapOptions(alts: Hotel[]): SwapOption[] {
  return (alts ?? []).map((h, i) => {
    const stars = (h as { starRating?: number; stars?: number })?.starRating ?? (h as { stars?: number })?.stars
    const o = h as { specialPrice?: number; standardPrice?: number; pricePerNight?: number; price?: { amount?: number }; rating?: number }
    const nightly = o.specialPrice ?? o.standardPrice ?? o.pricePerNight ?? o.price?.amount ?? null
    const rating = typeof o.rating === 'number' ? o.rating.toFixed(1) : null
    return { key: `h-${i}`, title: (h as { name?: string })?.name ?? 'Hotel', sub: [stars ? `${Math.round(stars)}★` : null, rating].filter(Boolean).join(' · '), price: money(nightly) }
  })
}

interface TimelineStopProps {
  when: string
  icon: React.ReactNode
  showLine?: boolean
  title: string
  pill?: React.ReactNode
  children: React.ReactNode
}
function TimelineStop({ when, icon, showLine, title, pill, children }: TimelineStopProps) {
  return (
    <div className="vmt-tl-item">
      <div className="vmt-tl-rail">
        <div className="vmt-node">{icon}</div>
        {showLine && <div className="vmt-line" />}
      </div>
      <div className="vmt-tl-c">
        <div className="vmt-tl-when">{when}</div>
        <div className="vmt-tl-card">
          <div className="vmt-hyb-h">
            <span className="vmt-hyb-ttl">{title}</span>
            {pill}
          </div>
          {children}
        </div>
      </div>
    </div>
  )
}

// ---- per-component renderers --------------------------------------------
function FlightStop({
  comp, leg, when, showLine, onSwap,
}: { comp: ComponentResult; leg: 'outbound' | 'return'; when: string; showLine?: boolean; onSwap?: (altIndex: number) => void }) {
  const f = comp.primary as FlightResult | null
  const loading = comp.state === 'loading'
  const held = comp.state === 'hold_recheck'
  // A refine can filter a component down to zero matches. Show a clear empty
  // state (not a blank "Flight / —" card) so the user knows to relax a filter.
  const empty = !loading && !held && !f
  if (empty) {
    return (
      <TimelineStop when={when} icon={<PlaneIcon />} showLine={showLine} title={`Flight · ${leg}`} pill={<MatchScorePill label="no match" variant="muted" />}>
        <div className="vmt-empty">{comp.error ? `Couldn't load flights: ${comp.error}` : 'No flights match these filters. Remove one above to see options.'}</div>
      </TimelineStop>
    )
  }
  const slices = (f as { slices?: Array<{ origin?: string; destination?: string; departureTime?: string; airline?: string; stops?: number }> })?.slices
  const slice = slices?.[leg === 'outbound' ? 0 : 1] ?? slices?.[0]
  const airline = slice?.airline ?? (f as { airline?: string })?.airline ?? '—'
  const route = slice ? `${slice.origin ?? ''} → ${slice.destination ?? ''}` : 'Flight'
  const stops = slice?.stops ?? 0
  const title = `Flight · ${leg}`
  const pill = loading
    ? <MatchScorePill label="re-ranking" variant="muted" />
    : held ? null
    : <MatchScorePill score={(f as { match_score?: number })?.match_score} />

  return (
    <TimelineStop
      when={when + (loading ? ' · RELOADING' : held ? ' · HELD' : '')}
      icon={<PlaneIcon />}
      showLine={showLine}
      title={held ? 'Flight · unchanged' : title}
      pill={pill}
    >
      <div className="vmt-hyb-body">
        <div className="vmt-hyb-l">
          <div className="vmt-opt">
            <div className="vmt-logo">{airline.slice(0, 2).toUpperCase()}</div>
            <div>
              <div className="vmt-nm">{route}</div>
              <div className={cn('vmt-sub', held && 'vmt-sub--warn')}>
                {held ? comp.warning : `${airline} · ${stops === 0 ? 'nonstop' : `${stops} stop`}`}
              </div>
            </div>
          </div>
          {!held && <Reasons reasons={(f as { match_reasons?: string[] })?.match_reasons} />}
        </div>
        {f && <div className="vmt-hyb-r"><Price p={flightPriceParts(f)} /></div>}
      </div>
      {!loading && !held && leg === 'outbound' && onSwap && (
        <AlternativesSwap options={flightSwapOptions((comp.alternatives ?? []) as FlightResult[])} onPick={onSwap} />
      )}
    </TimelineStop>
  )
}

function HotelStop({ comp, when, showLine, onSwap }: { comp: ComponentResult; when: string; showLine?: boolean; onSwap?: (altIndex: number) => void }) {
  const h = comp.primary as Hotel | null
  const loading = comp.state === 'loading'
  const held = comp.state === 'hold_recheck'
  const empty = !loading && !held && !h
  if (empty) {
    return (
      <TimelineStop when={when} icon={<BedIcon />} showLine={showLine} title="Hotel" pill={<MatchScorePill label="no match" variant="muted" />}>
        <div className="vmt-empty">{comp.error ? `Couldn't load hotels: ${comp.error}` : 'No hotels match these filters. Remove one above to see options.'}</div>
      </TimelineStop>
    )
  }
  const stars = (h as { starRating?: number; stars?: number })?.starRating ?? (h as { stars?: number })?.stars
  const pill = loading
    ? <MatchScorePill label="re-ranking" variant="muted" />
    : held ? null
    : <MatchScorePill score={(h as { match_score?: number })?.match_score} />
  return (
    <TimelineStop
      when={when + (loading ? ' · RELOADING' : held ? ' · HELD' : '')}
      icon={<BedIcon />}
      showLine={showLine}
      title={held ? 'Hotel · unchanged' : 'Hotel'}
      pill={pill}
    >
      <div className="vmt-hyb-body">
        <div className="vmt-hyb-l">
          <div className="vmt-opt">
            <div className="vmt-logo vmt-logo--img" aria-hidden="true" />
            <div>
              <div className="vmt-nm">{(h as { name?: string })?.name ?? 'Hotel'}</div>
              <div className={cn('vmt-sub', held && 'vmt-sub--warn')}>
                {held ? comp.warning : [stars ? `${Math.round(stars)}★` : null, typeof (h as { rating?: number })?.rating === 'number' ? (h as { rating?: number }).rating!.toFixed(1) : null].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
          {!held && <Reasons reasons={(h as { match_reasons?: string[] })?.match_reasons} />}
        </div>
        {h && <div className="vmt-hyb-r"><Price p={hotelPriceParts(h)} /></div>}
      </div>
      {!loading && !held && onSwap && (
        <AlternativesSwap options={hotelSwapOptions((comp.alternatives ?? []) as Hotel[])} onPick={onSwap} />
      )}
    </TimelineStop>
  )
}

function ItineraryStop({ comp, when, showLine }: { comp: ComponentResult; when: string; showLine?: boolean }) {
  const it = comp.primary as ItineraryDraft | null
  const chips = it?.day_chips ?? []
  const loading = comp.state === 'loading'
  const held = comp.state === 'hold_recheck'
  const pill = loading
    ? <MatchScorePill label="re-ranking" variant="muted" />
    : held ? null
    : <MatchScorePill label="draft" variant="purple" />
  return (
    <TimelineStop
      when={when + (loading ? ' · RELOADING' : held ? ' · HELD' : '')}
      icon={<PinIcon />}
      showLine={showLine}
      title={held ? 'Itinerary · unchanged' : 'Itinerary'}
      pill={pill}
    >
      <div className="vmt-hyb-body">
        <div className="vmt-hyb-l">
          {held && comp.warning ? (
            <div className="vmt-sub vmt-sub--warn">{comp.warning}</div>
          ) : (
            <div className="vmt-chips">
              {chips.map((c, i) => <span key={`${i}:${c}`} className={cn('vmt-pill', i === 1 && 'vmt-pill--gold')}>{c}</span>)}
            </div>
          )}
        </div>
      </div>
      {!loading && !held && <RedraftPlaceholder />}
    </TimelineStop>
  )
}

// ---- main view -----------------------------------------------------------
export interface CompoundResultsViewProps {
  session: VoyMyTripSession
  onSubmitUtterance: (utterance: string) => void
  onConstraintChange: (next: RefinementConstraintSet) => void
  /** Swap an alternative into the primary slot for a component. */
  onSwapAlternative?: (type: 'flights' | 'hotels', altIndex: number) => void
  onSaveTrip?: () => void
  onBook?: () => void
  changedTypes?: ConstraintType[]
  /** Static typed text for the mid-refinement design state (optional). */
  typedText?: string
}

export function CompoundResultsView({
  session, onSubmitUtterance, onConstraintChange, onSwapAlternative, onSaveTrip, onBook, changedTypes, typedText,
}: CompoundResultsViewProps) {
  const { framing, components } = session
  if (!framing || !session.constraintSet) return null

  const scope = framing.components
  const single = scope.length === 1
  const destination = framing.destination ?? 'your trip'
  const nights = nightsBetween(framing.depart_date, framing.return_date)

  // Readiness: resolved (ready/unchanged/hold) components over scope size.
  const readyCount = scope.filter((c) => {
    const st = components[c]?.state
    return st === 'ready' || st === 'unchanged' || st === 'hold_recheck'
  }).length

  // Eyebrow + title adapt to scope.
  const eyebrow = single
    ? `VOY MY TRIP · ${scope[0] === 'flights' ? 'FLIGHT' : scope[0] === 'hotels' ? 'HOTEL' : 'PLAN'}`
    : 'VOY MY TRIP · ASSEMBLED'
  const title = single && scope[0] === 'flights'
    ? `Flights to ${destination}`
    : single && scope[0] === 'hotels'
      ? `Stays in ${destination}`
      : `${nights ?? ''}${nights ? ' days in ' : ''}${destination}`

  // Build timeline stops in chronological order.
  const flight = components.flights
  const hotel = components.hotels
  const itinerary = components.itinerary
  const flightSlices = (flight?.primary as { slices?: unknown[] } | null)?.slices
  const flightRoundTrip = Array.isArray(flightSlices) && flightSlices.length > 1

  const stops: React.ReactNode[] = []
  if (scope.includes('flights') && flight) {
    stops.push(<FlightStop key="f-out" comp={flight} leg="outbound" when={`${fmtDate(framing.depart_date)} · OUTBOUND`} showLine onSwap={onSwapAlternative ? (i) => onSwapAlternative('flights', i) : undefined} />)
  }
  if (scope.includes('hotels') && hotel) {
    stops.push(<HotelStop key="hotel" comp={hotel} when={`${fmtDate(framing.depart_date)}–${fmtDate(framing.return_date)} · STAY`} showLine onSwap={onSwapAlternative ? (i) => onSwapAlternative('hotels', i) : undefined} />)
  }
  if (scope.includes('itinerary') && itinerary) {
    stops.push(<ItineraryStop key="itin" comp={itinerary} when="DAILY · PLAN" showLine />)
  }
  if (scope.includes('flights') && flight && flightRoundTrip) {
    stops.push(<FlightStop key="f-ret" comp={flight} leg="return" when={`${fmtDate(framing.return_date)} · RETURN`} />)
  }

  // Footer totals.
  const totalLabel = single
    ? `${scope[0] === 'flights' ? 'Flight' : scope[0] === 'hotels' ? 'Hotel' : 'Trip'} total · ${framing.pax_adults} pax`
    : `Trip total · ${framing.pax_adults} pax`
  const tripTotal = computeTotal(components, scope, nights)
  const bookLabel = single ? 'Add to cart' : 'Book whole trip'

  return (
    <div className="vmt-results">
      <div className="vmt-frame">
        {/* Header */}
        <div className="vmt-tframe">
          <div>
            <div className="vmt-eye"><span className="vmt-eye__dot" />{eyebrow}</div>
            <h2 className="vmt-title">{titleWithSerif(title, destination)}</h2>
            {framing && session.provenance?.narrative && (
              <div className="vmt-why"><WhyIcon /><span>{session.provenance.narrative}</span></div>
            )}
          </div>
          <div className="vmt-meta">
            <div className="vmt-meta__row"><span className="vmt-k">Dates</span>{fmtDate(framing.depart_date)} → {fmtDate(framing.return_date)}</div>
            <div className="vmt-meta__row"><span className="vmt-k">Pax</span>{framing.pax_adults} adults</div>
            <div className="vmt-meta__row"><span className="vmt-k">From</span>{framing.origin ?? '—'}</div>
            <div className="vmt-meta__row vmt-meta__stream">
              <span className="vmt-streaming"><span className="vmt-streaming__dot" />{readyCount} of {scope.length} ready</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="vmt-tl">{stops}</div>

        {/* Refinement (the live system) */}
        <RefinementBar
          constraintSet={session.constraintSet}
          onSubmitUtterance={onSubmitUtterance}
          onConstraintChange={onConstraintChange}
          changedTypes={changedTypes}
          typedText={typedText}
          notice={session.streaming ? undefined : session.lastMessage}
          changeLog={session.changeLog}
          disabled={session.streaming}
        />

        {/* Footer */}
        <div className="vmt-rfoot">
          <div className="vmt-tot">
            <span className="vmt-tot__lbl">{totalLabel}</span>
            <span className="vmt-tot__v">
              {money(tripTotal.total) ?? '—'}
              {tripTotal.saved != null && tripTotal.saved > 0 && <span className="vmt-tot__save">saved {money(tripTotal.saved)}</span>}
            </span>
          </div>
          <div className="vmt-cta">
            <button type="button" className="vmt-btn vmt-btn--ghost" onClick={onSaveTrip}>Save trip</button>
            <button type="button" className="vmt-btn vmt-btn--primary" onClick={onBook}><CartIcon />{bookLabel}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Render the destination word in serif-italic within the title (design .si). */
function titleWithSerif(title: string, destination: string): React.ReactNode {
  const idx = title.lastIndexOf(destination)
  if (idx < 0) return title
  return (
    <>
      {title.slice(0, idx)}
      <span className="vmt-si">{destination}</span>
      {title.slice(idx + destination.length)}
    </>
  )
}

function computeTotal(
  components: VoyMyTripSession['components'],
  scope: string[],
  nights: number | null,
): { total: number | null; saved: number | null } {
  let total = 0
  let saved = 0
  let any = false
  // Charge the member (special) price where it's a genuine discount, else the
  // standard; accumulate savings = standard − member. Same logic as the cards.
  const resolve = (standard: number | null, special: number | null) => {
    const has = standard != null || special != null
    const discount = standard != null && special != null && special < standard
    return { has, pay: (discount ? special : (standard ?? special)) as number, save: discount ? (standard as number) - (special as number) : 0 }
  }
  if (scope.includes('flights')) {
    const f = components.flights?.primary as { standardPrice?: number; totalPrice?: number; price?: number; specialPrice?: number; memberPrice?: number } | null
    const r = resolve(f?.standardPrice ?? f?.totalPrice ?? f?.price ?? null, f?.specialPrice ?? f?.memberPrice ?? null)
    if (f && r.has) { any = true; total += r.pay; saved += r.save }
  }
  if (scope.includes('hotels')) {
    const h = components.hotels?.primary as { standardPrice?: number; pricePerNight?: number; specialPrice?: number; memberPrice?: number; price?: { amount?: number } } | null
    const n = nights ?? 1
    const r = resolve(h?.standardPrice ?? h?.pricePerNight ?? h?.price?.amount ?? null, h?.specialPrice ?? h?.memberPrice ?? null)
    if (h && r.has) { any = true; total += r.pay * n; saved += r.save * n }
  }
  return { total: any ? total : null, saved: saved > 0 ? saved : null }
}
